import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress,
  LinearProgress, Card, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Chip, Grid,
  Container, Paper,
} from '@mui/material';
import {
  Autorenew, CancelOutlined, OpenInNew,
  CheckCircle, HourglassEmpty, ErrorOutline,
  DiamondOutlined, CalendarMonth, WarningAmberRounded,
  Stars, AccessTime, CreditCard, Layers,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getMerchantSubscription, cancelAutopay } from '../../../../services/subscriptionAPI';
import { merchantAuthAPI } from '../../../../services/merchantAPI';
import useRouteMeta from '../../../../hooks/useRouteMeta';

const PURPLE      = '#6318C6';
const PURPLE_DARK = '#5210a8';
const PURPLE_SOFT = 'rgba(99,24,198,0.07)';

// ── small helpers ─────────────────────────────────────────────────────────────

function StatBox({ icon, label, value, sub }) {
  return (
    <Box sx={{ bgcolor: '#fff', px: { xs: 2, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <Box sx={{
          width: 22, height: 22, borderRadius: '6px',
          bgcolor: PURPLE_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: { xs: 17, sm: 20 }, fontWeight: 800, color: '#1E2939', fontFamily: 'Montserrat, sans-serif', lineHeight: 1.15 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.4 }}>{sub}</Typography>
      )}
    </Box>
  );
}

function DetailRow({ label, value, valueColor }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f3f4f6' }}>
      <Typography sx={{ fontSize: 12.5, color: '#9ca3af', fontWeight: 500, flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: valueColor || '#1E2939', textAlign: 'right', ml: 2 }}>{value || '—'}</Typography>
    </Box>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MySubscriptionPage() {
  useRouteMeta();
  const navigate = useNavigate();

  const [loading, setLoading]                   = useState(true);
  const [sub, setSub]                           = useState(null);
  const [recentlyExpired, setRecentlyExpired]   = useState(null);
  const [tokenBalance, setTokenBalance]         = useState(0);
  const [totalTokens, setTotalTokens]           = useState(0);
  const [activeAddons, setActiveAddons]         = useState([]);
  const [cancelLoading, setCancelLoading]       = useState(false);
  const [confirmOpen, setConfirmOpen]           = useState(false);
  const [cancelTarget, setCancelTarget]         = useState(null); // null = plan, { addon_id, addon_name, expiry_date } = addon
  const [resultDialog, setResultDialog]         = useState({ open: false, success: true, msg: '' });
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);

  useEffect(() => {
    merchantAuthAPI.checkAuthVisibility()
      .then(r => { if (typeof r.data?.show_whatsapp_sms_auth === 'boolean') setShowWhatsAppSmsAuth(r.data.show_whatsapp_sms_auth); })
      .catch(() => {});
  }, []);

  const load = async () => {
    try {
      const res = await getMerchantSubscription();
      setSub(res.data?.subscription || null);
      setRecentlyExpired(res.data?.recently_expired || null);
      setTokenBalance(res.data?.token_balance || 0);
      setTotalTokens(res.data?.total_initial_tokens || 0);
      setActiveAddons(res.data?.active_addons || []);
    } catch {
      // empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCancelAutopay = async () => {
    setConfirmOpen(false);
    setCancelLoading(true);
    const isAddon = !!cancelTarget;
    const label   = isAddon ? (cancelTarget.addon_name || 'add-on') : 'plan';
    try {
      await cancelAutopay(isAddon ? cancelTarget.addon_id : undefined);
      load();
      setResultDialog({
        open: true, success: true,
        msg: `Autopay for ${label} has been cancelled. It stays active until the current period ends.`,
      });
    } catch (e) {
      setResultDialog({
        open: true, success: false,
        msg: e.response?.data?.error || `We were unable to cancel autopay for ${label}. Please try again.`,
      });
    } finally {
      setCancelLoading(false);
      setCancelTarget(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: PURPLE }} />
      </Box>
    );
  }

  // ── derived values ──────────────────────────────────────────────────────────
  const tokenPct      = totalTokens > 0 ? Math.min(100, Math.round((tokenBalance / totalTokens) * 100)) : 0;
  const tokensUsed    = Math.max(0, totalTokens - tokenBalance);
  const barColor      = tokenPct < 20 ? '#ef4444' : tokenPct < 50 ? '#f59e0b' : PURPLE;
  const fmtDate       = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
  const fmtDateShort  = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const expiryStr     = fmtDate(sub?.expiry_date);
  const startStr      = fmtDateShort(sub?.start_date);
  const graceExpiryStr = fmtDate(sub?.grace_expiry_date);
  const isGrace       = sub?.status === 'grace';
  const billingLabel  = sub?.billing_period === 'yearly' ? 'Yearly'
    : sub?.billing_period === 'monthly' ? 'Monthly'
    : sub?.billing_period || '';
  const renewalLabel  = sub?.renewal_type
    ? sub.renewal_type.charAt(0).toUpperCase() + sub.renewal_type.slice(1)
    : null;

  return (
    <Container className="mt-mb-0" maxWidth="lg" sx={{ mt: { xs: 0, sm: 0, md: 4 }, mb: { xs: 0, sm: 2, md: 4 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 0.5, sm: 2, md: 4 }, pt: { xs: 0, sm: 3, md: 3 }, boxShadow: 'none', border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' }, bgcolor: { xs: 'transparent', sm: 'white', md: 'white' }, borderRadius: '8px' }}>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} px={{ xs: 0.5, sm: 0 }}>
        <Button onClick={() => navigate(-1)}
          startIcon={
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
              <path d="M6.41309 0.916079L0.916291 6.41287L6.41309 11.9097" stroke="#651BC6" strokeWidth="1.83226" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
          sx={{ color: '#4A5565', px: 0, textTransform: 'none', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '17.59px', '&:hover': { background: 'transparent', opacity: 0.8 } }}>
          Back
        </Button>
        <Typography variant="h6" sx={{ color: '#1E2939', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.59px' }}>My Subscription</Typography>
      </Box>

      {/* ── No plan ──────────────────────────────────────────────────────────── */}
      {!sub && !recentlyExpired && (
        <Box sx={{
          textAlign: 'center', py: 10,
          border: '1.5px dashed #e5e7eb', borderRadius: '16px', bgcolor: '#fafafa',
        }}>
          <Box sx={{ fontSize: 52, mb: 2 }}>📦</Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#374151', mb: 0.75 }}>
            No active plan
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#6b7280', mb: 3.5, maxWidth: 320, mx: 'auto' }}>
            Subscribe to a plan to start generating AI-powered product photos.
          </Typography>
          {showWhatsAppSmsAuth && <Button variant="contained" onClick={() => navigate('/credits?open=1')}
            sx={{ bgcolor: PURPLE, borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 4, py: 1.2, '&:hover': { bgcolor: PURPLE_DARK } }}>
            Browse Plans
          </Button>}
        </Box>
      )}

      {/* ── Recently expired ─────────────────────────────────────────────────── */}
      {!sub && recentlyExpired && (
        <Box sx={{ border: '1px solid #fde68a', bgcolor: '#fffbeb', borderRadius: '16px', p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ErrorOutline sx={{ color: '#b45309', fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#92400e' }}>
              {recentlyExpired.status === 'cancelled' ? 'Plan Cancelled' : 'Plan Expired'}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, color: '#78350f', mb: 3, lineHeight: 1.7 }}>
            Your <strong>{recentlyExpired.plan_name}</strong> plan{' '}
            {recentlyExpired.status === 'cancelled' ? 'was cancelled' : 'expired'} on{' '}
            {new Date(recentlyExpired.expiry_date || recentlyExpired.updated_at)
              .toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}.
          </Typography>
          {showWhatsAppSmsAuth && <Button variant="contained" onClick={() => navigate('/credits?open=1')}
            sx={{ bgcolor: PURPLE, borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, '&:hover': { bgcolor: PURPLE_DARK } }}>
            Renew Plan
          </Button>}
        </Box>
      )}

      {/* ── Active plan — two-column layout ──────────────────────────────────── */}
      {sub && (
        <Grid container spacing={3} alignItems="stretch">

          {/* ── LEFT: main plan card ── */}
          <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>

            {/* Grace alert — above the card */}
            {isGrace && (
              <Box sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                bgcolor: '#fffbeb', border: '1px solid #fde68a',
                borderRadius: '14px', p: 2.5, mb: 2.5,
              }}>
                <WarningAmberRounded sx={{ color: '#d97706', fontSize: 22, mt: 0.1, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                    Your plan has expired — grace period is active
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#78350f', lineHeight: 1.7 }}>
                    You have view-only access until <strong>{graceExpiryStr}</strong>. Renew before then to restore full access and token generation.
                  </Typography>
                  {showWhatsAppSmsAuth && <Button size="small" variant="contained"
                    onClick={() => navigate('/credits?open=1')}
                    sx={{ mt: 1.5, bgcolor: '#d97706', borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: 13, px: 2.5, '&:hover': { bgcolor: '#b45309' } }}>
                    Renew Now
                  </Button>}
                </Box>
              </Box>
            )}

            {/* Plan hero card */}
            <Card elevation={0} sx={{
              borderRadius: '16px', overflow: 'hidden',
              border: '1px solid rgba(99,24,197,0.15)',
              boxShadow: '0 4px 24px rgba(99,24,197,0.08)',
              flex: 1, display: 'flex', flexDirection: 'column',
            }}>

              {/* Gradient header */}
              <Box sx={{
                background: 'linear-gradient(135deg, #6318C6 0%, #9333ea 55%, #F59E0B 100%)',
                px: { xs: 2, sm: 2.5 }, py: { xs: 2.5, sm: 3 },
              }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: 1.5, textTransform: 'uppercase', mb: 1 }}>
                  Current Plan
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                      <DiamondOutlined sx={{ color: '#fff', fontSize: 22 }} />
                      <Typography sx={{ fontSize: { xs: 22, sm: 28 }, fontWeight: 800, color: '#fff', fontFamily: 'Montserrat, sans-serif', lineHeight: 1 }}>
                        {sub.plan_name || 'Subscription'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {/* Status badge */}
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        px: 1.2, py: 0.4, borderRadius: '20px', fontSize: 11, fontWeight: 700,
                        bgcolor: sub.status === 'active' ? 'rgba(34,197,94,0.2)' : isGrace ? 'rgba(245,158,11,0.25)' : 'rgba(239,68,68,0.25)',
                        border: `1px solid ${sub.status === 'active' ? 'rgba(34,197,94,0.5)' : isGrace ? 'rgba(245,158,11,0.6)' : 'rgba(239,68,68,0.5)'}`,
                        color: sub.status === 'active' ? '#86efac' : isGrace ? '#fde68a' : '#fca5a5',
                      }}>
                        {sub.status === 'active'
                          ? <CheckCircle sx={{ fontSize: 12 }} />
                          : isGrace ? <HourglassEmpty sx={{ fontSize: 12 }} />
                          : <ErrorOutline sx={{ fontSize: 12 }} />}
                        {sub.status === 'active' ? 'Active' : isGrace ? 'Grace Period' : 'Expired'}
                      </Box>
                      {/* Billing badge */}
                      {billingLabel && (
                        <Box sx={{
                          display: 'inline-flex', px: 1.2, py: 0.4, borderRadius: '20px',
                          fontSize: 11, fontWeight: 700,
                          bgcolor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff',
                        }}>
                          {billingLabel}
                        </Box>
                      )}
                      {/* Expiry info */}
                      {!isGrace && expiryStr && (
                        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                          · Valid until <strong style={{ color: '#fff' }}>{expiryStr}</strong>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {showWhatsAppSmsAuth && <Button
                    size="small" variant="outlined"
                    endIcon={<OpenInNew sx={{ fontSize: 12 }} />}
                    onClick={() => navigate('/credits?open=1')}
                    sx={{
                      textTransform: 'none', fontWeight: 700, fontSize: 12.5, whiteSpace: 'nowrap',
                      borderColor: 'rgba(255,255,255,0.5)', color: '#fff',
                      borderRadius: '10px', py: 0.7, px: 2, flexShrink: 0,
                      bgcolor: 'rgba(255,255,255,0.12)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', borderColor: '#fff' },
                    }}
                  >
                    Change Plan
                  </Button>}
                </Box>
              </Box>

              {/* Stats row — gap-as-divider: parent bgcolor = divider colour, children bgcolor = white */}
              {(() => {
                const tileCount = (totalTokens > 0 ? 1 : 0) + 1 + (isGrace ? 2 : 1);
                const colSm     = tileCount;
                // On mobile (2-col grid) an odd tile count leaves a blank cell —
                // span the last tile across both columns to fill the gap.
                const lastSpanXs = tileCount % 2 === 1 ? 'span 2' : 'span 1';
                return (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${colSm}, 1fr)` },
                    gap: '1px', bgcolor: '#f0f0f0',
                    borderBottom: '1px solid #f0f0f0',
                  }}>
                    {totalTokens > 0 && (
                      <StatBox
                        icon={<Stars sx={{ fontSize: 13, color: PURPLE }} />}
                        label="Token Balance"
                        value={tokenBalance.toLocaleString('en-IN')}
                        sub={`of ${totalTokens.toLocaleString('en-IN')}`}
                      />
                    )}
                    <StatBox
                      icon={<Autorenew sx={{ fontSize: 13, color: PURPLE }} />}
                      label="Billing Cycle"
                      value={billingLabel || '—'}
                      sub="per period"
                    />
                    {isGrace ? (
                      <>
                        <StatBox
                          icon={<CalendarMonth sx={{ fontSize: 13, color: '#6b7280' }} />}
                          label="Plan Expired"
                          value={expiryStr || '—'}
                        />
                        <Box sx={{ gridColumn: { xs: lastSpanXs, sm: 'span 1' } }}>
                          <StatBox
                            icon={<AccessTime sx={{ fontSize: 13, color: '#d97706' }} />}
                            label="Grace Ends"
                            value={graceExpiryStr || '—'}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ gridColumn: { xs: lastSpanXs, sm: 'span 1' } }}>
                        <StatBox
                          icon={<CalendarMonth sx={{ fontSize: 13, color: PURPLE }} />}
                          label="Expires On"
                          value={expiryStr || '—'}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Token usage bar */}
              {totalTokens > 0 && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5, borderBottom: '1px solid #f3f4f6' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: 12.5, color: '#374151', fontWeight: 600 }}>Token Usage</Typography>
                    <Typography sx={{ fontSize: 12.5, color: barColor, fontWeight: 700 }}>{tokenPct}% remaining</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={tokenPct}
                    sx={{
                      height: 10, borderRadius: 5, bgcolor: '#f3f4f6',
                      '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 5 },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                      Used: <strong style={{ color: '#6b7280' }}>{tokensUsed.toLocaleString('en-IN')}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                      Allocated: <strong style={{ color: '#6b7280' }}>{totalTokens.toLocaleString('en-IN')}</strong>
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Autopay ON */}
              {sub.autopay_enabled && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.25, mt: 'auto' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 40, height: 40, borderRadius: '12px',
                        bgcolor: PURPLE_SOFT,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <Autorenew sx={{ fontSize: 20, color: PURPLE }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: PURPLE }}>
                          Autopay is ON
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                          Renews every {sub.billing_period === 'yearly' ? 'year' : 'month'}
                          {expiryStr && ` · Next on ${expiryStr}`}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      size="small" variant="outlined" color="error"
                      startIcon={cancelLoading ? <CircularProgress size={12} color="inherit" /> : <CancelOutlined sx={{ fontSize: 14 }} />}
                      disabled={cancelLoading}
                      onClick={() => { setCancelTarget(null); setConfirmOpen(true); }}
                      sx={{
                        textTransform: 'none', fontWeight: 600, fontSize: 12.5,
                        borderRadius: '9px', whiteSpace: 'nowrap', flexShrink: 0,
                        py: 0.7, px: 1.75,
                      }}
                    >
                      {cancelLoading ? 'Cancelling…' : 'Cancel Autopay'}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Autopay OFF — cancelled */}
              {!sub.autopay_enabled && sub.autopay_status === 'cancelled' && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                  <CancelOutlined sx={{ fontSize: 14, color: '#d1d5db' }} />
                  <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
                    Autopay is OFF · plan won't renew automatically after {expiryStr}.
                  </Typography>
                </Box>
              )}

              {/* Autopay paused */}
              {!sub.autopay_enabled && sub.autopay_status === 'paused' && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                  <HourglassEmpty sx={{ fontSize: 14, color: '#d97706' }} />
                  <Typography sx={{ fontSize: 12, color: '#d97706', fontWeight: 600 }}>
                    Autopay Paused · Your recurring billing is temporarily suspended.
                  </Typography>
                </Box>
              )}

              {/* Autopay halted */}
              {!sub.autopay_enabled && sub.autopay_status === 'halted' && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75, mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutline sx={{ fontSize: 14, color: '#ef4444' }} />
                    <Typography sx={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                      Autopay Halted · Payments have failed. Update your payment method.
                    </Typography>
                  </Box>
                  {showWhatsAppSmsAuth && <Button size="small" variant="outlined" color="error"
                    onClick={() => navigate('/credits?open=1')}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: '8px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    Browse Plans
                  </Button>}
                </Box>
              )}

              {/* Autopay completed */}
              {!sub.autopay_enabled && sub.autopay_status === 'completed' && (
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                  <CheckCircle sx={{ fontSize: 14, color: '#22c55e' }} />
                  <Typography sx={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>
                    Subscription cycle completed · No automatic renewal.
                  </Typography>
                </Box>
              )}

            </Card>
          </Grid>

          {/* ── RIGHT: detail + quick-action cards ── */}
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Subscription Details */}
            <Card elevation={0} sx={{
              borderRadius: '16px', border: '1px solid #e5e7eb',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)', p: 2.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: PURPLE_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CreditCard sx={{ fontSize: 16, color: PURPLE }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E2939' }}>Subscription Details</Typography>
              </Box>
              <DetailRow label="Start Date"   value={startStr} />
              <DetailRow label="Plan Code"    value={sub.plan_code} valueColor={PURPLE} />
              <DetailRow label="Currency"     value={sub.currency_code} />
              <DetailRow label="Renewal Type" value={renewalLabel} />
              {sub.billing_period === 'lifetime' && (
                <DetailRow label="Billing"    value="Lifetime · no renewal" />
              )}
            </Card>

            {/* Quick action — change / browse plans */}
            <Card elevation={0} sx={{
              borderRadius: '16px', border: '1px solid #e5e7eb',
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              bgcolor: '#fff',
              p: 2.5, flex: 1,
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: PURPLE_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Layers sx={{ fontSize: 16, color: PURPLE }} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E2939' }}>Plans & Add-ons</Typography>
                </Box>
                <Typography sx={{ fontSize: 12.5, color: '#6b7280', mb: 2.5, lineHeight: 1.6 }}>
                  Upgrade your plan, purchase token packs, or explore add-ons to extend your capabilities.
                </Typography>
              </Box>
              {showWhatsAppSmsAuth && <Button fullWidth variant="contained" onClick={() => navigate('/credits?open=1')}
                endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                sx={{
                  bgcolor: PURPLE, borderRadius: '10px', textTransform: 'none',
                  fontWeight: 700, fontSize: 13, py: 1,
                  '&:hover': { bgcolor: PURPLE_DARK },
                }}>
                Browse Plans
              </Button>}
            </Card>

          </Grid>
        </Grid>
      )}

      {/* ── Active Add-ons ────────────────────────────────────────────────────── */}
      {activeAddons.length > 0 && (
        <Box sx={{ mt: 3.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: PURPLE_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Layers sx={{ fontSize: 16, color: PURPLE }} />
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1E2939', fontFamily: 'Montserrat, sans-serif' }}>
              Active Add-ons
            </Typography>
            <Chip label={activeAddons.length} size="small"
              sx={{ bgcolor: PURPLE_SOFT, color: PURPLE, fontWeight: 700, fontSize: 11, height: 22, '& .MuiChip-label': { px: 1.25 } }} />
          </Box>
          <Grid container spacing={2}>
            {activeAddons.map((addon) => (
              <Grid item xs={12} sm={6} md={4} key={addon.sub_id || addon.addon_id}>
                <Card elevation={0} sx={{
                  borderRadius: '14px', border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.04)', p: 2.25,
                  display: 'flex', flexDirection: 'column', gap: 1,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E2939', lineHeight: 1.3 }}>
                      {addon.addon_name || addon.addon_code}
                    </Typography>
                    <Chip
                      label={addon.status || 'active'}
                      size="small"
                      sx={{
                        bgcolor: addon.status === 'active' ? '#f0fdf4' : '#f9fafb',
                        color: addon.status === 'active' ? '#15803d' : '#6b7280',
                        border: `1px solid ${addon.status === 'active' ? '#bbf7d0' : '#e5e7eb'}`,
                        fontWeight: 700, fontSize: 10, height: 20, flexShrink: 0,
                        textTransform: 'capitalize',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </Box>
                  {addon.addon_category && (
                    <Typography sx={{ fontSize: 11, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      {addon.addon_category}
                    </Typography>
                  )}
                  {addon.expiry_date && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                      <CalendarMonth sx={{ fontSize: 13, color: '#9ca3af' }} />
                      <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                        {addon.autopay_enabled
                          ? `Renews ${fmtDateShort(addon.expiry_date)}`
                          : `Expires ${fmtDateShort(addon.expiry_date)}`}
                      </Typography>
                    </Box>
                  )}
                  {addon.autopay_enabled && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Autorenew sx={{ fontSize: 13, color: PURPLE }} />
                        <Typography sx={{ fontSize: 11, color: PURPLE, fontWeight: 600 }}>
                          Auto-renews {addon.billing_period === 'yearly' ? 'yearly' : 'monthly'}
                        </Typography>
                      </Box>
                      <Button
                        size="small" variant="text" color="error"
                        disabled={cancelLoading}
                        sx={{ fontSize: 11, fontWeight: 600, textTransform: 'none', py: 0, px: 1, minWidth: 0 }}
                        onClick={() => {
                          setCancelTarget({ addon_id: addon.addon_id, addon_name: addon.addon_name, expiry_date: addon.expiry_date });
                          setConfirmOpen(true);
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                  {!addon.autopay_enabled && addon.autopay_status === 'cancelled' && (
                    <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>Autopay cancelled</Typography>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ── Cancel Autopay Confirmation Dialog ─────────────────────────────────── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 0.5 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <WarningAmberRounded sx={{ color: '#ef4444', fontSize: 20 }} />
            </Box>
            <Typography fontWeight={700} fontSize={16}>Cancel Autopay?</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, pb: 1.5 }}>
          <Typography fontSize={13} color="text.secondary" lineHeight={1.7}>
            {cancelTarget
              ? `Turn off automatic renewal for ${cancelTarget.addon_name || 'this add-on'}?`
              : 'Are you sure you want to turn off automatic renewal for your plan?'}
          </Typography>
          <Box sx={{ bgcolor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', p: 1.75, mt: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.75}>
              <CheckCircle sx={{ fontSize: 14, color: '#22c55e' }} />
              <Typography fontSize={12} color="text.secondary">
                {cancelTarget ? 'Add-on' : 'Your plan'} stays <strong>fully active</strong> until {cancelTarget ? fmtDateShort(cancelTarget.expiry_date) : (expiryStr || 'the end of the current period')}.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.75}>
              <CheckCircle sx={{ fontSize: 14, color: '#22c55e' }} />
              <Typography fontSize={12} color="text.secondary">
                All your tokens and features continue working.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <CancelOutlined sx={{ fontSize: 14, color: '#f59e0b' }} />
              <Typography fontSize={12} color="text.secondary">
                Will <strong>not renew</strong> after {cancelTarget ? fmtDateShort(cancelTarget.expiry_date) : (expiryStr || 'expiry')}. You can re-subscribe anytime.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined" color="inherit"
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, flex: 1, borderColor: '#e5e7eb', color: '#374151' }}>
            Keep Autopay
          </Button>
          <Button onClick={handleCancelAutopay} variant="contained" color="error"
            disabled={cancelLoading}
            startIcon={cancelLoading ? <CircularProgress size={13} color="inherit" /> : <CancelOutlined sx={{ fontSize: 14 }} />}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, flex: 1 }}>
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Result Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={resultDialog.open} onClose={() => setResultDialog(d => ({ ...d, open: false }))}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 0.5 } }}>
        <DialogContent sx={{ pt: 3, pb: 2, textAlign: 'center' }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
            bgcolor: resultDialog.success ? '#f0fdf4' : '#fef2f2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {resultDialog.success
              ? <CheckCircle sx={{ fontSize: 28, color: '#22c55e' }} />
              : <ErrorOutline sx={{ fontSize: 28, color: '#ef4444' }} />}
          </Box>
          <Typography fontWeight={700} fontSize={16} mb={1}>
            {resultDialog.success ? 'Autopay Cancelled' : 'Something Went Wrong'}
          </Typography>
          <Typography fontSize={13} color="text.secondary" lineHeight={1.7}>
            {resultDialog.msg}
          </Typography>
          {resultDialog.success && expiryStr && (
            <Chip
              icon={<CalendarMonth sx={{ fontSize: 13 }} />}
              label={`Active until ${expiryStr}`}
              size="small"
              sx={{ mt: 2, bgcolor: '#f0fdf4', color: '#15803d', fontWeight: 600, fontSize: 11, border: '1px solid #bbf7d0' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2.5 }}>
          <Button onClick={() => setResultDialog(d => ({ ...d, open: false }))} variant="contained"
            sx={{ bgcolor: resultDialog.success ? PURPLE : '#ef4444', borderRadius: '8px', textTransform: 'none', fontWeight: 700, px: 4, '&:hover': { filter: 'brightness(0.9)' } }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      </Paper>
    </Container>
  );
}
